// Centralized error handling utilities
import { logger } from './logger';

export interface ApiError {
  message: string;
  status?: number;
  code?: string;
}

export class AppError extends Error {
  public status: number;
  public code: string;

  constructor(message: string, status: number = 500, code: string = 'UNKNOWN_ERROR') {
    super(message);
    this.name = 'AppError';
    this.status = status;
    this.code = code;
  }
}

export const handleApiError = (error: any): ApiError => {
  if (error instanceof AppError) {
    return {
      message: error.message,
      status: error.status,
      code: error.code
    };
  }

  if (error instanceof TypeError && error.message.includes('fetch')) {
    return {
      message: 'Network error. Please check your connection and try again.',
      status: 0,
      code: 'NETWORK_ERROR'
    };
  }

  if (error.status) {
    switch (error.status) {
      case 400:
        return {
          message: 'Invalid request. Please check your input and try again.',
          status: 400,
          code: 'BAD_REQUEST'
        };
      case 401:
        return {
          message: 'Your session has expired. Please log in and try again.',
          status: 401,
          code: 'UNAUTHORIZED'
        };
      case 403:
        return {
          message: 'Access denied. You don\'t have permission to perform this action.',
          status: 403,
          code: 'FORBIDDEN'
        };
      case 404:
        return {
          message: 'Resource not found. The requested item may have been deleted or moved.',
          status: 404,
          code: 'NOT_FOUND'
        };
      case 429:
        return {
          message: 'Too many requests. Please wait a moment and try again.',
          status: 429,
          code: 'RATE_LIMITED'
        };
      case 500:
        return {
          message: 'Server error. Our team has been notified and will fix this soon.',
          status: 500,
          code: 'SERVER_ERROR'
        };
      default:
        return {
          message: 'An unexpected error occurred. Please try again.',
          status: error.status,
          code: 'UNKNOWN_ERROR'
        };
    }
  }

  return {
    message: error.message || 'An unexpected error occurred. Please try again.',
    status: 500,
    code: 'UNKNOWN_ERROR'
  };
};

export const logError = (error: any, context?: string) => {
  logger.error('Application Error', error, { context });
};

export const createApiRequest = async (
  url: string,
  options: RequestInit = {}
): Promise<any> => {
  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      // Try to get error message from response body
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorData = await response.json();
        if (errorData.error || errorData.message) {
          errorMessage = errorData.error || errorData.message;
        }
      } catch (e) {
        // If response body isn't JSON, keep the default message
      }

      // Special handling for authentication endpoints
      if (response.status === 401 && url.includes('/api/login')) {
        errorMessage = 'Invalid email or password. Please check your credentials and try again.';
      } else if (response.status === 409 && url.includes('/api/register')) {
        errorMessage = 'An account with this email already exists. Please try logging in instead.';
      } else if (response.status === 400 && url.includes('/api/register')) {
        errorMessage = errorMessage.includes('email') || errorMessage.includes('password') || errorMessage.includes('business') 
          ? errorMessage 
          : 'Please check your information and try again.';
      }

      throw new AppError(
        errorMessage,
        response.status,
        'HTTP_ERROR'
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    logError(error, `API Request to ${url}`);
    throw error;
  }
};